// src/pages/Settings.tsx
import React, { useMemo, useState } from 'react';
import { Layout, Menu, Button, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  ApartmentOutlined,
  TeamOutlined,
  TagsOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import StandardPage from '../components/Layout/StandardPage';
import ActivityTypeManager from '../components/settings/ActivityTypeManager';
import SectorManager from '../components/settings/SectorManager';
import ContactActivityTypeManager from '../components/settings/ContactActivityTypeManager';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;

type SectionKey = 'customer-activities' | 'contact-activities' | 'customer-sectors';

const Settings: React.FC = () => {
  const [section, setSection] = useState<SectionKey>('customer-activities');
  const [collapsed, setCollapsed] = useState(false);

  const items = useMemo<MenuProps['items']>(() => ([
    {
      key: 'customer-activities',
      icon: <ApartmentOutlined />,
      label: 'Attività Cliente',
    },
    {
      key: 'contact-activities',
      icon: <TeamOutlined />,
      label: 'Attività Contatto',
    },
    {
      key: 'customer-sectors',
      icon: <TagsOutlined />,
      label: 'Settori Cliente',
    },
  ]), []);

  const renderSection = () => {
    switch (section) {
      case 'customer-activities':
        return <ActivityTypeManager />;
      case 'contact-activities':
        return <ContactActivityTypeManager />;
      case 'customer-sectors':
        return <SectorManager />;
      default:
        return null;
    }
  };

  const getSectionTitle = () => {
    switch (section) {
      case 'customer-activities':
        return 'Attività Cliente';
      case 'contact-activities':
        return 'Attività Contatto';
      case 'customer-sectors':
        return 'Settori Cliente';
      default:
        return '';
    }
  };

  const getSectionSubtitle = () => {
    switch (section) {
      case 'customer-activities':
        return 'Gestisci le tipologie di attività utilizzate per i clienti';
      case 'contact-activities':
        return 'Gestisci le tipologie di attività utilizzate per i contatti';
      case 'customer-sectors':
        return 'Crea e gestisci i settori associabili ai clienti';
      default:
        return '';
    }
  };

  return (
    <Layout hasSider style={{ minHeight: '100vh', background: 'transparent' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={260}
        collapsedWidth={64}
        breakpoint="lg"
        onBreakpoint={(broken) => broken && setCollapsed(true)}
        theme="light"
        style={{
          background: '#fff',
          borderRight: '1px solid #f0f0f0',
          position: 'sticky',
          top: 0,
          height: '100dvh', // migliore su desktop/mobile moderni
          overflow: 'auto',
        }}
      >
        {/* Header del Sider */}
        <div
          style={{
            height: 81,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 12px',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            background: '#fff',
            zIndex: 1,
          }}
        >
          {!collapsed ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Title level={5} style={{ margin: 0, letterSpacing: '-0.01em' }}>
                Impostazioni
              </Title>
              <Text type="secondary" style={{ fontSize: 12, marginTop: -2 }}>
                Configura la tua organizzazione
              </Text>
            </div>
          ) : (
            <div  />
          )}

          <Button
            type="text"
            aria-label={collapsed ? 'Espandi menu' : 'Comprimi menu'}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              color: '#6b7280',
              borderRadius: 8,
            }}
          />
        </div>

        {/* Menu nativo AntD: gestisce automaticamente icone/label/tooltip in collapsed */}
        <Menu
          mode="inline"
          items={items}
          selectedKeys={[section]}
          onClick={({ key }) => setSection(key as SectionKey)}
          inlineCollapsed={collapsed}
          style={{
            borderInlineEnd: 'none',
            padding: 8,
            marginTop: 15
          }}
        />
      </Sider>

      <Layout style={{ background: 'transparent', padding:24 }}>
        <Content>
          <StandardPage title={getSectionTitle()} subtitle={getSectionSubtitle()}>
            {renderSection()}
          </StandardPage>
        </Content>
      </Layout>
    </Layout>
  );
};

export default Settings;
